/**
 * Per-segment health for the review page (TOADO-307).
 *
 * One failed clip is not a failed walkthrough. Before this, a segment whose
 * clip or transcript died was indistinguishable on screen from one still
 * working, so the page said "still finishing" indefinitely and the person
 * concluded their tickets were lost — which is exactly what happened in
 * TOADO-277, where a dead ffprobe job read as ongoing work for hours.
 *
 * The system already knows what went wrong. These codes exist in the schema
 * (`clip_invalid`, `media_timeout`, `transcript_last_error_code`) and reach
 * the client. They simply were not shown to the person affected.
 */

export type SegmentHealthKind = 'ok' | 'working' | 'clip_failed' | 'transcript_failed';

export interface SegmentHealth {
  kind: SegmentHealthKind;
  /** True when this segment can still become a ticket. */
  committable: boolean;
  /** Plain statement of what happened, or undefined when nothing is wrong. */
  message?: string;
  /** Underlying code, for support. Not shown as the primary message. */
  errorCode?: string;
}

export interface SegmentHealthInput {
  droppedAt?: string | null;
  /**
   * `expired` falls through to `ok`, and that is the right reading: a clip
   * pruned at 45 days (TOADO-367) is not a failure and not work in
   * progress. It belongs to a walkthrough that was committed long ago, so
   * there is nothing here left to wait for or to warn about.
   */
  clipUploadStatus?: 'pending' | 'uploading' | 'ready' | 'failed' | 'expired' | 'expiring' | null;
  transcriptStatus?: 'not_requested' | 'pending' | 'ready' | 'failed' | null;
  transcriptLastErrorCode?: string | null;
}

/**
 * A clip failure costs the narration for that moment; a transcript failure
 * costs only the text. Neither should block the other segments, and neither
 * should stop a ticket being filed — the screenshot and the description are
 * still there, and a ticket with a missing clip beats no ticket at all.
 */
export function segmentHealth(
  segment: SegmentHealthInput,
  transcriptionAvailable: boolean,
): SegmentHealth {
  if (segment.droppedAt) return { kind: 'ok', committable: false };

  if (segment.clipUploadStatus === 'failed') {
    return {
      kind: 'clip_failed',
      // Still committable on purpose. The moment keeps its screenshot and
      // draft; losing the video is a degraded ticket, not a lost one.
      committable: true,
      message: 'The video clip for this moment could not be produced. The ticket can still be created with its screenshot and notes.',
    };
  }

  if (transcriptionAvailable && segment.transcriptStatus === 'failed') {
    return {
      kind: 'transcript_failed',
      committable: true,
      message: 'Voice transcription failed for this moment. The clip and screenshot are unaffected.',
      ...(segment.transcriptLastErrorCode ? { errorCode: segment.transcriptLastErrorCode } : {}),
    };
  }

  const clipWorking = segment.clipUploadStatus === 'pending' || segment.clipUploadStatus === 'uploading';
  const transcriptWorking = transcriptionAvailable && segment.transcriptStatus === 'pending';
  if (clipWorking || transcriptWorking) return { kind: 'working', committable: true };

  return { kind: 'ok', committable: true };
}

export interface WalkthroughHealth {
  /** Segments that failed in some way and are worth naming on screen. */
  failed: number;
  /** Segments still being processed. */
  working: number;
  /** Segments that will produce a ticket on commit. */
  committable: number;
  /**
   * True when every live segment failed its clip. The walkthrough is not
   * recoverable by waiting, and the page should offer discard rather than a
   * spinner.
   */
  totalLoss: boolean;
}

export function walkthroughHealth(
  segments: SegmentHealthInput[],
  transcriptionAvailable: boolean,
): WalkthroughHealth {
  const live = segments.filter((segment) => !segment.droppedAt);
  const health = live.map((segment) => segmentHealth(segment, transcriptionAvailable));
  const failed = health.filter((item) => item.kind === 'clip_failed' || item.kind === 'transcript_failed').length;
  return {
    failed,
    working: health.filter((item) => item.kind === 'working').length,
    committable: health.filter((item) => item.committable).length,
    totalLoss: live.length > 0 && health.every((item) => item.kind === 'clip_failed'),
  };
}
